import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dataService, Logger } from '../services/data';
import { useSites } from '../hooks/useSites';
import Card from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';
import StatusBadge from '../components/ui/StatusBadge';

export default function Sites() {
  const { t } = useTranslation();
  const { data: sites, isLoading } = useSites();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!sites || sites.length === 0) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('site.title')}</h1>
        <Card>
          <p className="text-gray-500 text-center py-8">{t('common.noData')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('site.title')}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sites.map((site) => (
          <SiteCard key={site.id} siteId={site.id} />
        ))}
      </div>
    </div>
  );
}

interface SiteCardProps {
  siteId: string;
}

function SiteCard({ siteId }: SiteCardProps) {
  const { t } = useTranslation();
  const { data: site, error: siteError } = useQuery({
    queryKey: ['sites', siteId],
    queryFn: () => dataService.getSite(siteId),
    retry: false,
  });

  const { data: loggers = [] } = useQuery<Logger[], Error>({
    queryKey: ['loggers', siteId],
    queryFn: () => dataService.getLoggers(siteId),
    enabled: !!site && !siteError,
    retry: false,
  });

  // Si erreur d'accès (403), ne rien afficher
  if (siteError || !site) {
    return null;
  }

  // Determine site status based on loggers
  const getStatus = () => {
    if (loggers.length === 0) return 'offline';
    const hasOffline = loggers.some((logger) => !logger.is_active);
    if (hasOffline) return 'warning';
    return 'ok';
  };

  const status = getStatus();

  return (
    <Link to={`/sites/${siteId}`}>
      <Card className="hover:shadow-lg transition-shadow">
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-semibold text-gray-900">{site.name}</h3>
            <StatusBadge status={status} />
          </div>

          {site.address && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">{t('site.address')}:</span> {site.address}
            </p>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <span className="text-sm text-gray-600">
              {t('site.loggersCount')}: <span className="font-semibold text-gray-900">{loggers.length}</span>
            </span>
            <span className={`text-xs ${site.is_active ? 'text-green-600' : 'text-gray-400'}`}>
              {site.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}
